// Phase 8 (plan 3.5 and 3.6): what the web reads from Omni's OAuth routes and
// works out from them. The record shapes are @phoneware/lens-domain's oauth.ts;
// the answers below are the 3.6 route shapes Main settled on 2026-10-18.
import {
  OAUTH_REQUEST_TTL_SECONDS,
  isAdminRole,
  isDealerRole,
  permissionsOf,
  roleLabel,
  type Dealer,
  type LensUser,
  type OAuthGrantRecord,
  type OAuthRequestRecord,
  type Role,
} from "@phoneware/lens-domain";
import { ApiError } from "./api.js";
import { seesPricesWith } from "./session.js";

/** GET /api/oauth/requests/:id, readable without signing in: the request without its PKCE challenge, state or TTL field. */
export type OAuthRequestView = Pick<OAuthRequestRecord, "clientId" | "clientName" | "redirectUri" | "scopes" | "createdAt"> & { id: string };

/** POST /api/oauth/requests/:id/decision, sent with the person's ID token. */
export interface OAuthDecisionRequest {
  allow: boolean;
}

/** The client's redirect: a code and its state on Allow, error=access_denied on Deny. */
export interface OAuthDecisionResult {
  redirectTo: string;
}

/** The 403 body the decision and the signed-in request read answer for someone whose Omni sign-in is turned off. */
export interface UserDisabledRefusal {
  error: string;
  code: "user_disabled";
  user: { name: string; role: Role; dealerName: string | null };
}

/** `revokedBy` on a grant Omni revoked itself because an old refresh token was used again. */
export const REVOKED_BY_REFRESH_REUSE = "omni:refresh_reuse";

/** Omni's MCP address, the resource every grant is for. */
export { OMNI_OAUTH_RESOURCE_MCP as MCP_URL } from "@phoneware/lens-domain";

/** When a consent request stops being answerable, in epoch ms. */
export function requestExpiresAt(r: Pick<OAuthRequestView, "createdAt">): number {
  return Date.parse(r.createdAt) + OAUTH_REQUEST_TTL_SECONDS * 1000;
}

/** "claude.ai" from the client's redirect address; the address itself when it does not parse. */
export function hostOf(uri: string): string {
  try {
    return new URL(uri).hostname || uri;
  } catch {
    return uri;
  }
}

/** The signed-out person a refusal names, when the refusal is "your sign-in is turned off". */
export function disabledUser(err: unknown): UserDisabledRefusal["user"] | null {
  if (!(err instanceof ApiError) || err.status !== 403) return null;
  const body = err.body;
  if (!body || typeof body !== "object" || !("code" in body) || body.code !== "user_disabled" || !("user" in body)) return null;
  const user = body.user;
  if (!user || typeof user !== "object" || !("name" in user) || typeof user.name !== "string" || !("role" in user)) return null;
  const dealerName = "dealerName" in user && typeof user.dealerName === "string" ? user.dealerName : null;
  return { name: user.name, role: user.role as Role, dealerName };
}

/** "Sales at TVC", or the tier ("Billing ops") for someone at Phoneware. */
export function roleAt(role: Role, dealerName: string | null): string {
  return dealerName && isDealerRole(role) ? `${roleLabel(role)} at ${dealerName}` : roleLabel(role);
}

/** What an app reading as this person sees, in the words of the consent card and the Connect tab. */
export function readScope(user: Pick<LensUser, "role" | "scope">, dealer: Pick<Dealer, "name"> | null): string {
  if (isAdminRole(user.role) || !dealer) return "Every dealer\u2019s customers, catalog, quote templates, quotes, orders and quote documents, as you see them.";
  // Tech sees customers and orders but not prices (Settings, People and roles), and an app acting as them sees the same.
  if (!seesPricesWith(permissionsOf(user.role))) return `${user.scope === "own" ? `Your own customers at ${dealer.name}` : `${dealer.name}\u2019s customers`} and their orders, without prices.`;
  if (user.scope === "own") return `Your own customers at ${dealer.name}, and the catalog, quote templates, quotes, orders and quote documents you see.`;
  return `${dealer.name}\u2019s customers, catalog, quote templates, quotes, orders and quote documents.`;
}

export const WRITE_SCOPE = "Add customers and contacts, start and change draft quotes, and hand Omni a quote document to read.";
export const CANT_DO = "Send a QuoteLink, mark a quote lost or sold, approve billing, or change the catalog, dealers or people.";

/** Connected first, then revoked, newest first within each. */
export function orderGrants(list: OAuthGrantRecord[]): OAuthGrantRecord[] {
  const newest = (a: string | null, b: string | null) => (b ?? "").localeCompare(a ?? "");
  const live = list.filter((g) => !g.revokedAt).sort((a, b) => newest(a.createdAt, b.createdAt));
  const gone = list.filter((g) => g.revokedAt).sort((a, b) => newest(a.revokedAt, b.revokedAt));
  return [...live, ...gone];
}
